/**
 * Custom archive loader for epub.js's `requestMethod` BookOptions hook.
 *
 * epub.js calls the returned function for every file it needs:
 *   - META-INF/container.xml → synthesized in-memory
 *   - content.opf            → synthesized in-memory from the manifest
 *   - <spine href>           → fetched lazily from /epubjs/spine/{bookId}/{idx}
 *   - <image|css|font path>  → fetched from /image?path=..., /css?path=..., /font?path=...
 *
 * Combined with `openAs: "directory"` and `replacements: "blobUrl"` on the
 * ePub() constructor, this ensures the full .epub is never assembled on the
 * client — every byte flows through authenticated per-resource endpoints.
 *
 * Signature the hook contract expects (from epub.js types/book.d.ts):
 *   (url: string, type: string, withCredentials: object, headers: object)
 *     => Promise<XMLDocument | string | object | ArrayBuffer>
 *
 * Return type depends on `type`:
 *   "xml" | "xhtml" | "html"  → XMLDocument
 *   "text"                    → string
 *   "json"                    → parsed object
 *   "binary"                  → ArrayBuffer
 */
import type { EpubJsManifestDto } from '../../api/epubjsService';
import { epubjsService } from '../../api/epubjsService';

/** Root of the virtual filesystem we feed to epub.js. Any path within it. */
export const VIRTUAL_ROOT = (bookId: string) => `/virtual/${bookId}/`;

/** Container.xml points at a synthetic OPF at the book root. */
const CONTAINER_XML = `<?xml version="1.0" encoding="UTF-8"?>
<container version="1.0" xmlns="urn:oasis:names:tc:opendocument:xmlns:container">
  <rootfiles>
    <rootfile full-path="content.opf" media-type="application/oebps-package+xml"/>
  </rootfiles>
</container>`;

export interface CustomArchiveLoaderArgs {
  bookId: string;
  manifest: EpubJsManifestDto;
}

/** Map of spine href → spine index, for O(1) dispatch from href-based requests. */
type SpineHrefIndex = Map<string, number>;

export function createCustomArchiveLoader({ bookId, manifest }: CustomArchiveLoaderArgs) {
  const spineHrefIndex: SpineHrefIndex = new Map(
    manifest.spineItems.map((s) => [s.href, s.spineIndex])
  );
  const virtualRoot = VIRTUAL_ROOT(bookId);

  const parser = new DOMParser();

  const synthesizeOpf = (): string => {
    const manifestItems = manifest.spineItems
      .map(
        (s) =>
          `    <item id="spine-${s.spineIndex}" href="${escapeXml(s.href)}" media-type="${escapeXml(s.mediaType)}"/>`
      )
      .join('\n');
    const spineItemrefs = manifest.spineItems
      .map((s) => `    <itemref idref="spine-${s.spineIndex}"/>`)
      .join('\n');
    return `<?xml version="1.0" encoding="UTF-8"?>
<package xmlns="http://www.idpf.org/2007/opf" version="3.0" unique-identifier="pub-id">
  <metadata xmlns:dc="http://purl.org/dc/elements/1.1/">
    <dc:identifier id="pub-id">${escapeXml(manifest.packageIdentifier)}</dc:identifier>
    <dc:title>${escapeXml(manifest.packageTitle)}</dc:title>
    <dc:language>${escapeXml(manifest.packageLanguage)}</dc:language>
    ${manifest.packageAuthor ? `<dc:creator>${escapeXml(manifest.packageAuthor)}</dc:creator>` : ''}
  </metadata>
  <manifest>
${manifestItems}
  </manifest>
  <spine>
${spineItemrefs}
  </spine>
</package>`;
  };

  /**
   * Decide how to serve a given path.
   */
  const dispatch = async (
    rawUrl: string,
    type: string
  ): Promise<XMLDocument | string | object | ArrayBuffer> => {
    // Normalize: epub.js resolves relative paths against window.location so
    // requestMethod can see either "/virtual/{bookId}/foo" (rare) or
    // "http://localhost:3001/virtual/{bookId}/foo" (the common case). Strip
    // the origin first, then peel the virtual-root prefix so downstream
    // matching works on the raw manifest href the backend knows about.
    let pathOnly = rawUrl;
    try {
      if (/^https?:\/\//i.test(rawUrl)) {
        const parsed = new URL(rawUrl);
        pathOnly = parsed.pathname + parsed.search;
      }
    } catch {
      // not a parseable URL — treat as-is
    }
    const relPath = pathOnly.startsWith(virtualRoot)
      ? pathOnly.slice(virtualRoot.length)
      : pathOnly;

    // 1. Container / OPF — both synthesized in memory.
    if (relPath === 'META-INF/container.xml' || relPath.endsWith('/META-INF/container.xml')) {
      return parser.parseFromString(CONTAINER_XML, 'application/xml');
    }
    if (relPath === 'content.opf' || relPath.endsWith('/content.opf')) {
      return parser.parseFromString(synthesizeOpf(), 'application/xml');
    }

    // 2. Spine item — look up by manifest href. Spine items are always XHTML
    // and must be returned as a parsed Document: epub.js calls book.load(path)
    // with type=null (see epubjs src/book.js load() — line `this.request(resolved,
    // null, ...)`) so we can't dispatch on the type arg. Using text/html parser
    // (HTML5, lenient) to match what the browser would do when the iframe
    // finally receives the content via srcdoc. Returning a string here would
    // make Section.load resolve to a string and every rendition render would
    // silently no-op.
    const spineIndex = spineHrefIndex.get(relPath);
    if (spineIndex !== undefined) {
      const dto = await epubjsService.getSpineItem(bookId, spineIndex);
      return parser.parseFromString(dto.xhtmlContent, 'text/html');
    }

    // 3. Asset — decide kind from extension and hit the corresponding endpoint.
    const kind = assetKindFromPath(relPath);
    if (kind) {
      return await epubjsService.getAssetBytes(kind, relPath);
    }

    // 4. Unknown path — surface loudly so we can patch the loader instead of
    // silently returning garbage.
    throw new Error(`customArchiveLoader: unhandled path "${relPath}" (type="${type}")`);
  };

  /**
   * The signature epub.js calls. The extra parameters (withCredentials, headers)
   * are ignored — we always route through the axios apiClient which handles
   * auth centrally.
   */
  return function requestMethod(
    url: string,
    type: string,
    _withCredentials?: object,
    _headers?: object
  ): Promise<XMLDocument | string | object | ArrayBuffer> {
    return dispatch(url, type);
  };
}

function assetKindFromPath(path: string): 'image' | 'css' | 'font' | null {
  const lower = path.toLowerCase();
  if (/\.(png|jpe?g|gif|webp|svg|bmp|ico)$/.test(lower)) return 'image';
  if (/\.css$/.test(lower)) return 'css';
  if (/\.(woff2?|ttf|otf|eot)$/.test(lower)) return 'font';
  return null;
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}
